import { useMemo } from 'react';
import type {
  Field,
  FieldValue,
  FileFieldValue,
  FormField,
  Option
} from '@elfsight/widget-form-builder/widget';
import type { ProcessedPaymentInfo } from '@elfsight/widget-stripe-payment';
import { useSettings } from '@wu/widget-core';
import { BOOKING_HIDDEN_FIELDS_IDS } from '../form/fields';
import { usePaymentData } from '../payment/payment-data-provider';

function formatFileField(value: FileFieldValue) {
  if (!Array.isArray(value)) return '';
  return value
    .filter((file) => file.uploaded)
    .map((file) => `<a href="${file.uploaded}">${file.name}</a>`)
    .join(', ');
}

function formatConsentField(value: string) {
  return value === 'Yes' ? value : 'No';
}

function formatMultiChoiceField(options: Option[], value: FieldValue) {
  if (!Array.isArray(value)) return '';
  return value
    .map(
      (optionID) =>
        options.find((option) => option.id === optionID)?.name ?? optionID
    )
    .join(', ');
}

function formatSingleChoiceField(options: Option[], value?: FieldValue) {
  if (value == null) return '';
  const found = options.find((option) => option.id === String(value));
  return found?.name ?? String(value);
}

function formatDefaultField(value?: FieldValue) {
  return String(value ?? '');
}

function formatFieldValue(field: Field, fieldSettings?: FormField) {
  if (field.type === 'file') {
    return formatFileField(field.value as FileFieldValue);
  }

  if (field.type === 'consent') {
    field.name = field.name?.replace(/<div>(.*?)<\/div>/, '$1');

    return formatConsentField(field.value as string);
  }

  if (fieldSettings?.options) {
    if (Array.isArray(field.value)) {
      return formatMultiChoiceField(fieldSettings.options, field.value);
    }

    return formatSingleChoiceField(fieldSettings.options, field.value);
  }

  return formatDefaultField(field.value);
}

function getPaymentDescription(
  processedPaymentInfo: Exclude<ProcessedPaymentInfo, undefined>
) {
  const {
    displayPrice,
    displayPaidAmount,
    displayDate,
    cardNumber,
    displayPaymentMethodType,
    chargeId,
    receiptUrl,
    refundUrl
  } = processedPaymentInfo;

  return [
    'Payment Status: Paid',
    `Price: ${displayPrice}`,
    `Amount Paid: ${displayPaidAmount}`,
    displayPaymentMethodType && `Payment Method: ${displayPaymentMethodType}`,
    cardNumber && `Card Last 4 Digits: ${cardNumber}`,
    chargeId && `Transaction ID: ${chargeId}`,
    `Payment Date: ${displayDate}`,
    receiptUrl && `<a href="${receiptUrl}">View Receipt</a>`,
    refundUrl && `<a href="${refundUrl}">Refund</a>`
  ]
    .filter(Boolean)
    .join('\n');
}

export function useCreateCalendarEventDescription(fieldsArray: Field[]) {
  const { fields } = useSettings();
  const { processedPaymentInfo } = usePaymentData();

  return useMemo(() => {
    const fieldSettingsById = Object.fromEntries(
      fields.map((fieldSettings) => [fieldSettings.id, fieldSettings])
    );

    const descriptionEntries = fieldsArray.reduce<string[]>((result, field) => {
      const isVisible =
        field.visible !== false &&
        !BOOKING_HIDDEN_FIELDS_IDS.includes(field.id);

      if (!isVisible) {
        return result;
      }

      const fieldSettings = fieldSettingsById[field.id];
      const formattedFieldValue = formatFieldValue(field, fieldSettings);
      const isFieldEmpty = !formattedFieldValue || !field.name;

      if (!isFieldEmpty) {
        result.push(`${field.name}: ${formattedFieldValue}`);
      }

      return result;
    }, []);

    if (processedPaymentInfo) {
      descriptionEntries.push(getPaymentDescription(processedPaymentInfo));
    }

    return descriptionEntries.join('\n');
  }, [fieldsArray, fields, processedPaymentInfo]);
}
